import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FAVOURITES } from "./homeData";
import { SectionHeading } from "./SectionHeading";
import { AfterpayToday } from "@/components/booking/PayLaterCalculator";

const FavouriteCard = ({ p, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: i * 0.1 }}
    whileHover={{ y: -6 }}
    className="group relative rounded-3xl overflow-hidden bg-dream-surface border border-white/10 hover:border-white/25 transition-colors flex flex-col"
    data-testid={`popular-card-${p.id}`}
  >
    <div className="relative h-60 overflow-hidden">
      <img src={p.image} alt={p.imageAlt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-dream-surface via-dream-surface/30 to-transparent" />
      <span className={`absolute top-4 left-4 px-3 py-1 rounded-full ${p.badgeAccent} text-white text-[10px] font-bold uppercase tracking-widest`}>{p.badge}</span>
    </div>
    <div className="p-7 flex flex-col flex-1">
      <p className="text-white/40 text-xs font-mono uppercase tracking-widest mb-2">{p.subtitle}</p>
      <h3 className="font-heading text-2xl font-bold text-white mb-4 leading-tight">{p.title}</h3>
      <div className="flex items-baseline gap-2">
        <span className={`text-transparent bg-clip-text bg-gradient-to-r ${p.accent} font-display text-4xl lg:text-5xl font-bold tracking-tight`}>${p.price}</span>
        <span className="text-white/40 text-xs">AUD</span>
      </div>
      <AfterpayToday price={p.price} className="mt-1 mb-5" testId={`popular-paylater-${p.id}`} />
      <ul className="space-y-2.5 mb-7">
        {p.perks.map((perk) => (
          <li key={perk} className="flex items-start gap-2 text-white/80 text-sm">
            <CheckCircle2 className="w-4 h-4 text-dream-violet flex-shrink-0 mt-0.5" />
            {perk}
          </li>
        ))}
      </ul>
      <Link to={`/booking?service=${p.id}`} className="mt-auto">
        <Button className={`w-full bg-gradient-to-r ${p.accent} hover:opacity-90 text-white font-semibold rounded-full py-6`} data-testid={`popular-cta-${p.id}`}>
          {p.ctaLabel}
          <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
        </Button>
      </Link>
    </div>
  </motion.div>
);

export const FavouritesSection = () => (
  <section className="relative py-24 md:py-32 bg-dream-bg overflow-hidden" data-testid="popular-services">
    <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-dream-violet/10 rounded-full blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 relative">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <SectionHeading eyebrow="Most Popular" eyebrowColor="text-yellow-400" title={<>Book the <span className="text-transparent bg-clip-text bg-gradient-to-r from-dream-violet to-cyan-400">favourites.</span></>} sub="The three packages our artists come back for. Straight to the point, priced right." />
        <Link to="/pricing" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1 shrink-0" data-testid="favourites-all-pricing-link">
          See all pricing <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {FAVOURITES.map((p, i) => <FavouriteCard key={p.id} p={p} i={i} />)}
      </div>
    </div>
  </section>
);
